import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganisation } from '../contexts/OrganisationContext';
import { OrganisationCreate } from '../types/api';
import toast from 'react-hot-toast';
import { BuildingOfficeIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { SuccessMessages, ErrorMessages, getApiErrorMessage } from '../constants/messages';

const OrganisationsPage: React.FC = () => {
  const { t } = (useTranslation as any)('organisations');
  const navigate = useNavigate();
  const location = useLocation();
  const { userOrganisations, organisationsLoading, createOrganisation, selectOrganisation } = useOrganisation();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState<OrganisationCreate>({
    nom: '',
    plan_type: 'FREE',
  });

  // Auto-open create modal if on /organisations/create path
  useEffect(() => {
    if (location.pathname === '/organisations/create') {
      setShowCreateModal(true);
    }
  }, [location]);

  const handleCreateOrganisation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nom.trim()) {
      toast.error(ErrorMessages.VALIDATION_ERROR);
      return;
    }

    setCreating(true);
    try {
      const newOrg = await createOrganisation(formData);
      toast.success(SuccessMessages.ORG_CREATED);
      setShowCreateModal(false);
      setFormData({ nom: '', plan_type: 'FREE' });

      // Navigate to the new organization dashboard
      navigate(`/organisations/${newOrg.id}/dashboard`);
    } catch {
      // Error toast is already shown by OrganisationContext
    } finally {
      setCreating(false);
    }
  };

  const handleSelectOrganisation = async (orgId: string) => {
    try {
      await selectOrganisation(orgId);
      navigate(`/organisations/${orgId}/dashboard`);
    } catch (error: unknown) {
      toast.error(ErrorMessages.ORG_LOAD_FAILED);
    }
  };

  const getPlanBadgeColor = (planType: string) => {
    switch (planType) {
      case 'FREE':
        return 'bg-surface-3 text-text-secondary';
      case 'PROFESSIONAL':
        return 'bg-purple-500/12 text-purple-400';
      case 'BUSINESS':
        return 'bg-indigo-500/12 text-indigo-400';
      case 'ENTERPRISE':
        return 'bg-yellow-500/12 text-yellow-400';
      default:
        return 'bg-surface-3 text-text-secondary';
    }
  };

  if (organisationsLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
          <p className="mt-4 text-text-secondary">{t('list.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">
              {t('list.title')}
            </h1>
            <p className="mt-2 text-text-secondary">
              {t('list.subtitle')}
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('list.createButton')}
          </button>
        </div>

        {/* Empty State */}
        {userOrganisations.length === 0 && (
          <div className="bg-surface-2 shadow-card rounded-card p-12 text-center">
            <BuildingOfficeIcon className="mx-auto h-16 w-16 text-text-quaternary" />
            <h3 className="mt-4 text-lg font-medium text-text-primary">
              {t('list.empty.title')}
            </h3>
            <p className="mt-2 text-sm text-text-tertiary">
              {t('list.empty.description')}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-600 hover:bg-accent-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              {t('list.createButton')}
            </button>
          </div>
        )}

        {/* Organizations Grid */}
        {userOrganisations.length > 0 && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {userOrganisations.map((org) => (
              <div
                key={org.id}
                className="bg-surface-2 overflow-hidden shadow-card rounded-card hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleSelectOrganisation(org.id)}
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <BuildingOfficeIcon className="h-8 w-8 text-accent-400" />
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPlanBadgeColor(org.plan_type)}`}>
                      {org.plan_type}
                    </span>
                  </div>

                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    {org.nom}
                  </h3>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-tertiary">{t('list.card.cpu')}</span>
                      <span className="font-medium text-text-primary">
                        {org.quota_global_cpu} vCPU
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-tertiary">{t('list.card.ram')}</span>
                      <span className="font-medium text-text-primary">
                        {org.quota_global_ram} GB
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-tertiary">{t('list.card.storage')}</span>
                      <span className="font-medium text-text-primary">
                        {org.quota_global_stockage} GB
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectOrganisation(org.id);
                      }}
                      className="w-full text-center text-sm font-medium text-accent-400 hover:text-accent-300"
                    >
                      {t('list.card.goToDashboard')} &rarr;
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Organization Modal */}
      {showCreateModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={() => setShowCreateModal(false)}></div>

            <div className="inline-block align-bottom bg-surface-2 rounded-card px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="absolute top-0 right-0 pt-4 pr-4">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="bg-surface-2 rounded-md text-text-quaternary hover:text-text-tertiary focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-text-primary mb-4">
                    {t('list.createModal.title')}
                  </h3>

                  <form onSubmit={handleCreateOrganisation} className="space-y-4">
                    <div>
                      <label htmlFor="nom" className="block text-sm font-medium text-text-secondary">
                        {t('list.createModal.nameLabel')}
                      </label>
                      <input
                        type="text"
                        id="nom"
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-3 text-text-primary focus:outline-none focus:ring-accent-500 focus:border-accent-500"
                        placeholder={t('list.createModal.namePlaceholder')}
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="plan_type" className="block text-sm font-medium text-text-secondary">
                        {t('list.createModal.planLabel')}
                      </label>
                      {/* Une organisation se crée TOUJOURS au plan Gratuit.
                          Les plans payants s'activent par NubieCloud après
                          accord commercial : il n'existe ni abonnement ni
                          encaissement, donc les proposer ici revenait à les
                          DONNER. Le serveur refuse désormais toute autre valeur
                          — ce champ ne fait que le refléter. */}
                      <select
                        id="plan_type"
                        value="FREE"
                        disabled
                        className="mt-1 block w-full border border-border-1 rounded-md shadow-sm py-2 px-3 bg-surface-2 text-text-secondary cursor-not-allowed"
                      >
                        <option value="FREE">{t('list.createModal.planFree')}</option>
                      </select>
                      <p className="mt-1 text-xs text-text-tertiary">
                        {t('list.createModal.planUpgradeHint')}
                      </p>
                    </div>

                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={creating}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent-600 text-base font-medium text-white hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                      >
                        {creating ? t('list.createModal.creating') : t('list.createModal.submit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCreateModal(false)}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-border-1 shadow-sm px-4 py-2 bg-surface-3 text-base font-medium text-text-secondary hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        {t('cancel', { ns: 'common' })}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrganisationsPage;
